import { useState, useEffect, useContext } from "react";
import axios from "axios";
import Preloader from "../../../shared/PreLoaders";
import { Authorization } from "../../../Context/Authorization";
import { AuthContext } from "../../../Context/AuthContext";
import { toast } from "react-toastify";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const ViewPayments = () => {
  const [accounts, setAccounts] = useState([]);
  const [voucherHeads, setVoucherHeads] = useState([]);
  const [vfcs, setVfcs] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedVfc, setSelectedVfc] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedVoucherHead, setSelectedVoucherHead] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [validationError, setValidationError] = useState("");
  const [noTransactionsMessage, setNoTransactionsMessage] = useState("");
  const [emptyDataMessage, setEmptyDataMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const Api = process.env.REACT_APP_API;
  const { selectedJfmcType } = useContext(AuthContext);

  const fetchVfcByJfmcType = async (jfmcTypeId) => {
    setLoading(true);
    try {
      const Config = Authorization();
      const response = await axios.post(`${Api}/api/vfcs/names`, { jfmcTypeId }, Config);
      setVfcs(response.data.vfcs || []);
    } catch (error) {
      console.error("Error fetching VFC data", error);
      toast.error("Failed to load VFCs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedJfmcType) fetchVfcByJfmcType(selectedJfmcType._id);
  }, [selectedJfmcType]);

  useEffect(() => {
    setLoading(true);
    const Config = Authorization();
    axios.get(`${Api}/api/accounts`, Config).then((response) => { setAccounts(response.data.data); });
    axios.get(`${Api}/api/voucher-types`, Config).then((response) => { setVoucherHeads(response.data.voucherTypes); });
    setLoading(false);
  }, []);

  const handleFilter = async () => {
    if (!selectedVfc && !selectedAccount && !selectedVoucherHead && !selectedDate) {
      setValidationError("Please select at least one filter option.");
      setTransactions([]);
      setEmptyDataMessage("");
      return;
    }
    setValidationError("");
    setEmptyDataMessage("");
    try {
      const data = {
        vfc: selectedVfc || undefined,
        date: selectedDate ? new Date(selectedDate).toISOString() : undefined,
        account: selectedAccount || undefined,
        voucherType: selectedVoucherHead || undefined,
      };
      const response = await axios.post(`${Api}/api/transactions/filter/transactions`, data);
      const paymentTransactions = response.data.data.filter((t) => t.type === "P");
      if (paymentTransactions.length === 0) {
        setTransactions([]);
        setNoTransactionsMessage("No payment transactions found.");
      } else {
        setTransactions(paymentTransactions);
        setNoTransactionsMessage("");
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setNoTransactionsMessage("Error fetching transactions.");
    }
  };

  const fieldFocus = (e) => (e.target.style.borderColor = "#4CAF50");
  const fieldBlur = (e) => (e.target.style.borderColor = "#E2E8F0");

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Filter Payments</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>Transactions</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>View Payments</span>
        </div>
      </div>

      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <>
          {/* ── Filter Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                <i className="mdi mdi-filter-variant" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Filter Options</span>
            </div>

            {validationError && (
              <div style={{ marginBottom: 14, padding: "8px 14px", background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, fontSize: 13, color: "#C62828", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="mdi mdi-alert-circle-outline" /> {validationError}
              </div>
            )}
            {emptyDataMessage && (
              <div style={{ marginBottom: 14, padding: "8px 14px", background: "#EBF8FF", border: "1px solid #BEE3F8", borderRadius: 8, fontSize: 13, color: "#2B6CB0", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="mdi mdi-information-outline" /> {emptyDataMessage}
              </div>
            )}
            {noTransactionsMessage && (
              <div style={{ marginBottom: 14, padding: "8px 14px", background: "#EBF8FF", border: "1px solid #BEE3F8", borderRadius: 8, fontSize: 13, color: "#2B6CB0" }}>
                {noTransactionsMessage}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Select JFMC</label>
                <select value={selectedVfc} onChange={(e) => setSelectedVfc(e.target.value)} style={inputStyle} onFocus={fieldFocus} onBlur={fieldBlur}>
                  <option value="">Select JFMC</option>
                  {vfcs.map((vfc) => <option key={vfc._id} value={vfc._id}>{vfc.vfcName}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Select Account</label>
                <select value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)} disabled={!selectedVfc} style={{ ...inputStyle, opacity: !selectedVfc ? 0.6 : 1 }} onFocus={fieldFocus} onBlur={fieldBlur}>
                  <option value="">Select Account</option>
                  {accounts.map((account) => <option key={account._id} value={account._id}>{account.accountName}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Select Voucher Head</label>
                <select value={selectedVoucherHead} onChange={(e) => setSelectedVoucherHead(e.target.value)} disabled={!selectedVfc} style={{ ...inputStyle, opacity: !selectedVfc ? 0.6 : 1 }} onFocus={fieldFocus} onBlur={fieldBlur}>
                  <option value="">Select Voucher Head</option>
                  {voucherHeads.map((vh) => <option key={vh._id} value={vh._id}>{vh.voucherName}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Select Date</label>
                <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={inputStyle} onFocus={fieldFocus} onBlur={fieldBlur} />
              </div>
            </div>

            <button onClick={handleFilter}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)" }}>
              <i className="mdi mdi-file-search" /> Filter Payments
            </button>
          </div>

          {/* ── Results Table ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
              <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
                <i className="mdi mdi-table-large" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", flex: 1 }}>Payment Transactions</span>
              <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 50 }}>{transactions.length} records</span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    {["Voucher Type", "Voucher Number", "Description", "Date", "Party Details", "Account", "Amount", "VFC", "Cash/Bank", "Docs"].map((h) => (
                      <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: "#4A5568", borderBottom: "1px solid #EDF2F7", background: "#F7FAFC", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => (
                    <tr key={transaction._id} style={{ borderBottom: "1px solid #EDF2F7" }}>
                      <td style={{ padding: "10px 14px", color: "#4A5568" }}>{transaction.voucherType?.voucherName || "N/A"}</td>
                      <td style={{ padding: "10px 14px", color: "#4A5568" }}>{transaction.voucherNumber}</td>
                      <td style={{ padding: "10px 14px", color: "#718096" }}>{transaction.description}</td>
                      <td style={{ padding: "10px 14px", color: "#4A5568" }}>{new Date(transaction.date).toLocaleDateString()}</td>
                      <td style={{ padding: "10px 14px", color: "#1A202C", fontWeight: 600 }}>{transaction.partyDetails}</td>
                      <td style={{ padding: "10px 14px", color: "#4A5568" }}>{transaction.accountId?.accountName}</td>
                      <td style={{ padding: "10px 14px", color: "#4A5568" }}>{transaction.amount}</td>
                      <td style={{ padding: "10px 14px", color: "#4A5568" }}>{transaction.vfcId?.vfcName}</td>
                      <td style={{ padding: "10px 14px", color: "#4A5568" }}>{transaction.cashOrBank}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <a href={`${Api.replace("/api", "")}/${transaction.uploadDocument}`} target="_blank" rel="noopener noreferrer"
                          style={{ color: "#1B5E20", fontWeight: 600, fontSize: 12 }}>View</a>
                      </td>
                    </tr>
                  ))}
                  {transactions.length === 0 && (
                    <tr><td colSpan={10} style={{ padding: "24px", textAlign: "center", color: "#A0AEC0", fontSize: 13 }}>No data to display</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ViewPayments;
